// ── The handful of words the design system says by itself ────────────────────
//
// Most text on screen belongs to a view, which translates it and passes it in.
// But a few strings live inside the components: the label on a modal's close
// cross, the one on a toast's dismiss, the reveal button in a password field,
// the header a screen reader reads over a column of buttons.
//
// Those were hardcoded English, which meant a Catalan user with a screen reader
// heard "Close" and "Show password" in the middle of an otherwise Catalan
// application — invisible to anyone testing with their eyes.
//
// They are not translated here: the ui-kit ships no translations, so it never
// has to be edited when a language is added. The shell hands them over at boot
// and again whenever the language changes, exactly like configureFormat().

const DEFAULTS = {
  close: 'Close',
  dismiss: 'Dismiss',
  cancel: 'Cancel',
  confirm: 'Confirm',
  actions: 'Actions',
  loading: 'Loading',
  // UiLoadState: la carga falló, y el botón que la repite.
  loadFailed: 'This could not be loaded',
  retry: 'Try again',
  empty: 'Nothing to show yet',
  showPassword: 'Show password',
  hidePassword: 'Hide password',
  // Las dos palabras de UiListHeader: la cabecera de una lista la dice el
  // sistema de diseño, no cada una de las cuarenta pantallas que la usan.
  filters: 'Filters',
  summary: 'Summary',
  filterOptions: 'Filter options',
  noOptions: 'No options',
  guideShow: 'Explain this screen',
  guideHide: 'Hide the explanations',
  // El tutorial de la función, que se abre en otra pestaña.
  tutorialOpen: 'How this works (opens in a new tab)',
  // Su nombre corto, cuando va junto al ? de las notas (ver UiPageHeader).
  tutorialShort: 'Open the manual',
  // The recorder and the player say every one of their own words: they are a
  // control, not a screen, and the module that drops one into a form should not
  // have to translate "Stop" for the fourth time.
  audioRecord: 'Record audio',
  audioStop: 'Stop',
  audioRetry: 'Record again',
  audioPlay: 'Play',
  audioPause: 'Pause',
  audioDelete: 'Delete the recording',
  // ── Why a device could not be reached ──────────────────────────────────────
  //
  // Five answers and not two, because they have five different fixes: allow it,
  // use another device, close whatever is holding it, open the page over https,
  // or give up. Collapsing them — which is what both callers used to do — sends
  // people looking in the wrong place. See composables/useMediaCapture.js.
  //
  // The camera has its own set rather than sharing a device-neutral one: a
  // screen can hold both, and «permission was refused» without saying for what
  // is a message somebody has to guess at.
  audioDenied: 'No access to the microphone. Allow it in the browser and try again.',
  audioMissing: 'No microphone was found on this device.',
  audioBusy: 'The microphone is being used by something else. Close it and try again.',
  audioInsecure: 'Recording needs a secure address (https). Open the ERP over https and try again.',
  audioUnsupported: 'This device cannot record audio.',
  cameraDenied: 'No access to the camera. Allow it in the browser and try again.',
  cameraMissing: 'No camera was found on this device.',
  cameraBusy: 'The camera is being used by something else. Close it and try again.',
  cameraInsecure: 'The camera needs a secure address (https). Open the ERP over https and try again.',
  cameraUnsupported: 'This device cannot use the camera.',
  // The picker and the viewer, for the same reason as the recorder: they are
  // controls rather than screens, and the four modules that attach photographs
  // should not each translate «Gallery» again.
  // The header box of a table that lets rows be chosen. It has no label beside
  // it — there is no room for one — so this is the whole of what a screen
  // reader has to go on.
  selectAll: 'Select every row',
  mediaPhoto: 'Photo',
  mediaVideo: 'Video',
  mediaGallery: 'Gallery',
  mediaRemove: 'Remove',
  mediaFull: 'No room for any more.',
  // `{max}` is replaced with the ceiling of the instance. Said before the
  // upload rather than after it: the upload is the part that costs a minute.
  mediaTooBig: 'That clip is over {max}. Film a shorter one.',
  viewerTitle: 'Attachment',
  viewerPrevious: 'Previous',
  viewerNext: 'Next',
  viewerDownload: 'Open the file',
  // The tag box says all of its own words too, for the same reason: a list of
  // VAT rates is a control that half a dozen screens drop in, and none of them
  // should have to translate "Remove" again.
  tagPlaceholder: 'Type and press Enter',
  tagMore: 'Add another',
  tagRemove: 'Remove',
  tagReorder: 'Drag to reorder',
  tagSuggestions: 'Also available:',
  tagUnknown: 'Not one of the available ones',
  tagRejected: 'This value will not be accepted',
  tagFull: 'The list is full.',
}

let labels = { ...DEFAULTS }

/**
 * Called by the shell with the translated set.
 *
 *   watch(locale, () => configureLabels({
 *     close: t('common.close'), dismiss: t('common.dismiss'), …
 *   }), { immediate: true })
 *
 * Unknown names are ignored rather than stored: a typo must not quietly become
 * the string a user reads.
 */
export function configureLabels(next = {}) {
  for (const [name, value] of Object.entries(next)) {
    if (name in DEFAULTS && typeof value === 'string' && value.trim()) {
      labels[name] = value
    }
  }
}

/** The current wording for one of them. Falls back to English, never to blank. */
export function uiLabel(name) {
  return labels[name] ?? DEFAULTS[name] ?? name
}

/** Back to English. Exists so tests do not leak state into one another. */
export function resetLabels() {
  labels = { ...DEFAULTS }
}

export const LABEL_NAMES = Object.keys(DEFAULTS)
